// libs
import { useAuthLogin, useFinishSignupMutation, useUser } from '@lopo-tech/lopo-api'
import React, { FunctionComponent, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

// components
import { Auth } from 'src/components/authentication/auth/auth'
import LoadingSpinner from 'src/components/loading_spinner/loading_spinner'

// constants
import { FRONTEND_CREATE_PROFILE, FRONTEND_SETTINGS_PATH, INTENT_CREATE_PUBLIC_PROFILE } from 'src/constants/urls'
import { useAuthContext } from 'src/context/authContext'

type IView = 'initial' | 'registration-form' | 'email-verification' | 'phone-verification' | 'phone-code-verification'

const useRegistrationState = () => {
  const { isAuthenticated } = useAuthLogin()
  const { data, loading } = useUser({ skip: !isAuthenticated })

  if (!isAuthenticated || loading || !data) {
    return { view: 'initial' as IView, loading: loading && isAuthenticated, email: '', phoneNumber: '', isAuthenticated, canFinish: false }
  }

  const me = data.me
  const currentProfile = data.currentProfile
  const isIntermediate = currentProfile?.__typename === 'IntermediateProfile'

  if (!isIntermediate) {
    return { view: 'initial' as IView, loading: false, email: '', phoneNumber: '', isAuthenticated, canFinish: false }
  }

  const hasConfirmedEmail = !!me.confirmedAt
  const hasVerifiedPhone = !!me.verifiedAt

  if (!hasConfirmedEmail) {
    return { view: 'email-verification' as IView, loading: false, email: me.email, phoneNumber: '', isAuthenticated, canFinish: false }
  }

  if (!hasVerifiedPhone && me.unverifiedPhone) {
    return { view: 'phone-code-verification' as IView, loading: false, email: me.email, phoneNumber: me.unverifiedPhone, isAuthenticated, canFinish: false }
  }

  if (!hasVerifiedPhone) {
    return { view: 'phone-verification' as IView, loading: false, email: me.email, phoneNumber: '', isAuthenticated, canFinish: false }
  }

  // Both email and phone verified — ready to finish signup
  return { view: 'initial' as IView, loading: false, email: me.email, phoneNumber: '', isAuthenticated, canFinish: true }
}

const Registration: FunctionComponent = () => {
  const { view, loading, email, phoneNumber, canFinish } = useRegistrationState()
  const [finishSignup] = useFinishSignupMutation()
  const { setSelectedProfileId } = useAuthContext()
  const history = useHistory()
  const location = useLocation()
  const intent = new URLSearchParams(location.search).get('intent')
  const isPublicProfileFlow = intent === INTENT_CREATE_PUBLIC_PROFILE

  useEffect(() => {
    if (canFinish) {
      finishSignup({ variables: { input: {} } })
        .then((result) => {
          const profileId = result.data?.finishSignup?.profileId
          if (profileId) {
            setSelectedProfileId(profileId)
          }
          if (isPublicProfileFlow) {
            history.replace(`${FRONTEND_CREATE_PROFILE}/profile`)
          } else {
            history.replace(FRONTEND_SETTINGS_PATH)
          }
        })
    }
  }, [canFinish])

  if (loading || canFinish) {
    return <LoadingSpinner shown />
  }

  return (
    <Auth
      closeModal={() => undefined}
      closeable={false}
      initialTab="register"
      initialView={view}
      initialEmail={email}
      initialPhoneNumber={phoneNumber}
      isPublicProfileFlow={isPublicProfileFlow}
    />
  )
}

export default Registration
